import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';

// project imports
import Avatar from 'components/@extended/Avatar';
import { useAuth } from 'contexts/AuthContext';
import { useGetMenuMaster } from 'api/menu';

// assets
import RightOutlined from '@ant-design/icons/RightOutlined';

interface ExpandMoreProps {
  expand?: boolean;
  drawerOpen?: boolean;
}

const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expand' && prop !== 'drawerOpen'
})<ExpandMoreProps>(({ theme }) => ({
  transform: 'rotate(-90deg)',
  marginLeft: 'auto',
  color: theme.vars?.palette.secondary.dark,
  transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
  variants: [
    { props: ({ expand }: ExpandMoreProps) => !expand, style: { transform: 'rotate(0deg)' } },
    { props: ({ drawerOpen }: ExpandMoreProps) => !drawerOpen, style: { opacity: 0, width: 50, height: 50 } }
  ]
}));

// ==============================|| DRAWER - USER ||============================== //

export default function NavUser() {
  const navigate = useNavigate();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const firstName = user?.nome?.split(' ')[0] || 'Usuário';
  const initials = firstName.charAt(0).toUpperCase();

  return (
    <Box
      sx={(theme) => ({
        p: 1.25,
        px: !drawerOpen ? 1.25 : 3,
        // Sem linha dura: leve sombra pra cima separa o usuário do menu (profundidade sutil).
        boxShadow: '0 -8px 18px -12px rgba(19, 29, 38, 0.16)',
        ...theme.applyStyles('dark', { boxShadow: '0 -1px 0 rgba(255, 255, 255, 0.05)' })
      })}
    >
      <List disablePadding>
        <ListItem
          disablePadding
          secondaryAction={
            <ExpandMore
              expand={open}
              drawerOpen={drawerOpen}
              id="nav-user-button"
              aria-controls={open ? 'nav-user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              aria-label="abrir menu do usuário"
            >
              <RightOutlined style={{ fontSize: '0.625rem' }} />
            </ExpandMore>
          }
          sx={{ '& .MuiListItemSecondaryAction-root': { right: !drawerOpen ? -20 : -16 } }}
        >
          <ListItemAvatar>
            <Avatar alt={user?.nome} src={user?.avatarUrl ?? undefined} sx={{ ...(drawerOpen && { width: 46, height: 46 }) }}>
              {initials}
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={user?.nome || 'Usuário'} secondary={user?.role || user?.email || ''} />
        </ListItem>
      </List>
      <Menu
        id="nav-user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { 'aria-labelledby': 'nav-user-button' } }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleLogout}>Sair</MenuItem>
      </Menu>
    </Box>
  );
}
